import React, { useMemo } from "react";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    LabelList,
} from "recharts";


// Datos tal cual llegan del backend
export type BackendItemCostKm = {
    vehicleId: string;
    plateNumber: string;
    brand: string;
    model: string;
    totalFuelCost: string | number; // € totales en el periodo
    totalDistanceTravelled: string | number; // km totales en el periodo
    costPerKm: string | number; // € / km
};


export type PropsCostKm = {
    title?: string;
    periodLabel: string;
    items?: BackendItemCostKm[];
    loading?: boolean;
    error?: string | null;
    height?: number; // px (default 320)
    className?: string;
    color?: string; // color de barra
    maxRows?: number; // top N (default 6)
    sort?: "asc" | "desc"; // ordenar por coste/km (default asc)
};

const fmtCurrency = (n: number) =>
    new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 4 }).format(n);
const fmtKm = (n: number) =>
    `${new Intl.NumberFormat("es-ES", { maximumFractionDigits: 0 }).format(n)} km`;


export const AverageConsumptionPerKilometrePerVehicleRanking: React.FC<PropsCostKm> = ({
                                                                   title = "Coste por kilómetro por vehículo",
                                                                   periodLabel,
                                                                   items = [],
                                                                   loading = false,
                                                                   error = null,
                                                                   height = 320,
                                                                   className = "",
                                                                   color = "#326e5a",
                                                                   maxRows = 6,
                                                                   sort = "asc",
                                                               }) => {
    const data = useMemo(() => {
        const parsed = (items || []).map((it) => ({
            id: it.vehicleId,
            name: `${it.plateNumber} • ${it.brand} ${it.model}`,
            value: Number.parseFloat(String(it.costPerKm)) || 0,
            totalCost: Number.parseFloat(String(it.totalFuelCost)) || 0,
            distance: Number.parseFloat(String(it.totalDistanceTravelled)) || 0,
        }));
        const ordered = parsed.sort((a, b) => (sort === "asc" ? a.value - b.value : b.value - a.value));
        return ordered.slice(0, maxRows);
    }, [items, maxRows, sort]);


    const noData = !loading && !error && data.length === 0;

    return (
        <div className={`rounded-2xl border bg-white shadow-sm ${className}`} style={{ minHeight: height }}>
            {/* Header */}
            <div className="p-4 flex items-center justify-between">
                <h3 className="font-semibold">{title}</h3>
                <div className="text-sm text-gray-600">{periodLabel}</div>
            </div>


            <div className="px-4 pb-6 relative">
                {loading ? (
                    <div className="p-6 text-sm text-gray-500">Cargando ranking…</div>
                ) : error ? (
                    <div className="p-6 text-sm text-rose-600">{error}</div>
                ) : noData ? (
                    <>
                        <div className="p-6 text-sm text-gray-500 opacity-40 blur-[2px] select-none">
                            (No hay elementos para mostrar)
                        </div>
                        <div className="absolute inset-0 z-10 flex items-center justify-center">
                            <div className="rounded-xl bg-white/80 px-4 py-2 text-sm text-gray-700 shadow">
                                No hay datos para el periodo seleccionado.
                            </div>
                        </div>
                    </>
                ) : (
                    <div style={{ height: height - 90 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={data}
                                layout="vertical"
                                margin={{ top: 8, right: 16, bottom: 0, left: 8 }}
                                barCategoryGap={12}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    type="number"
                                    tickLine={false}
                                    axisLine={false}
                                    tick={{ fontSize: 11 }}
                                    tickFormatter={(v) => fmtCurrency(Number(v))}
                                />
                                <YAxis
                                    type="category"
                                    dataKey="name"
                                    width={220}
                                    tickLine={false}
                                    axisLine={false}
                                    tick={{ fontSize: 12 }}
                                />
                                <Tooltip
                                    cursor={{ opacity: 0.08 }}
                                    formatter={(v: any, _name: any, payload: any) => [
                                        fmtCurrency(Number(v)),
                                        "Coste / km",
                                    ]}
                                    labelFormatter={(_label: any, payload: any) => {
                                        const p = payload?.[0]?.payload;
                                        if (!p) return "";
                                        return `${p.name}
${fmtCurrency(p.totalCost)} · ${fmtKm(p.distance)}`;
                                    }}
                                />
                                <Bar dataKey="value" radius={[8, 8, 8, 8]} fill={color}>
                                    <LabelList
                                        dataKey="value"
                                        position="right"
                                        formatter={(v: any) => fmtCurrency(Number(v))}
                                        className="text-[11px] fill-gray-700"
                                    />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>
        </div>
    );
};